import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

export interface ConfirmOptions {
  /** Defaults to a generic "Confirm" title. */
  title?: string;
  /** 'danger' renders the confirm button red (destructive actions like delete). */
  variant?: 'default' | 'danger';
}

type ConfirmFn = (message: string, options?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Drop-in async replacement for `window.confirm(message)` — themed, not a native browser popup. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within a ConfirmDialogProvider');
  }
  return ctx;
}

const ConfirmDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [state, setState] = useState<{ message: string; options: ConfirmOptions } | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((message, options = {}) => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setState({ message, options });
    });
  }, []);

  const handleClose = (result: boolean) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-40 px-4">
            <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 p-5">
              {state.options.title && (
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {state.options.title}
                </h3>
              )}
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{state.message}</p>
              <div className="flex justify-end gap-3 mt-5">
                <button
                  type="button"
                  onClick={() => handleClose(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  autoFocus
                  onClick={() => handleClose(true)}
                  className={`px-4 py-2 rounded-lg text-white text-sm transition-colors ${
                    state.options.variant === 'danger'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {t('common.confirm')}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </ConfirmContext.Provider>
  );
};

export default ConfirmDialogProvider;
